import React from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, defineMessages } from 'react-intl';
import Header from '../../components/Header';
import Segment from '../../components/Segment';
import Grid from '../../components/Grid';
import Toolbar from '../../components/CalendarToolbar/calendar-toolbar';
import PageTitle from '../../components/PageTitle';
import Event from '../../components/CalendarEvent';
import EventAgenda from '../../components/CalendarEventAgenda';
import DateTimeAgenda from '../RepVisitsCalendarEventDetailsAgenda/date-time';
import AvailabilityCalendar from '../AvailabilityCalendar';
import LeftPanel from '../../components/CalendarLeftPanel';
import SidebarWrapper from '../../components/SidebarWrapper';
import RepVisitsCalendarEventDetailsSideBar from '../RepVisitsCalendarEventDetailsSideBar';
import GoogleCalendar from '../GoogleCalendar';
import GoogleCalendarEventDetails from '../GoogleCalendarEventDetails';
import Toast from '../../components/Toast';
import { onFeatureEnabled, withEnabledFeatures } from '../../utils/features';
import './styles.css';

const messages = defineMessages({
  otherCalendars: {
    id: 'repvisits.calendar.otherCalendars',
    defaultMessage: 'Other Calendars',
  },
  detailsNotProvided: {
    id: 'repvisits.calendar.detailsNotProvided',
    defaultMessage: 'Not Provided',
  },
});

const actions = {
  GOOGLE_CALENDAR_DETAILS: 'GOOGLE_CALENDAR_DETAILS',
  CALENDAR_DRAWER: 'CALENDAR_DRAWER',
};

function formatDate(date) {
  return `${date.getFullYear()}-${date.getMonth()+1}-${date.getDate()}`;
}

function calculateRangeLength(firstDay, lastDay) {
  const rangeLength = parseInt(((lastDay - firstDay)/(1000*60*60*24)), 10);
  return Number.isNaN(rangeLength) ? 30 : rangeLength;
}

function getFromDate(currentDate) {
  const fromDateObj = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
  fromDateObj.setDate(1 - fromDateObj.getDay());
  return fromDateObj;
}

function getToDate(currentDate) {
  const toDateObj = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1);
  toDateObj.setDate(7 - toDateObj.getDay());
  return toDateObj;
}

export const timeGutterHeaderComponent = () => 'all day';

export const GoogleCalendarSection = ({ fromDate, toDate, onEvents, router }) => (
  <Grid.Row>
    <Grid.Column textAlign="left">
      <Header size="tiny" styleName="header" id="pageTitle">
        <FormattedMessage {...messages.otherCalendars} />
      </Header>
      <GoogleCalendar
        fromDate={fromDate}
        toDate={toDate}
        onEvents={onEvents}
        router={router}
      />
    </Grid.Column>
  </Grid.Row>
);

GoogleCalendarSection.propTypes = {
  fromDate: PropTypes.string,
  toDate: PropTypes.string,
  onEvents: PropTypes.func.isRequired,
  router: PropTypes.object,
};

const GoogleCalendarWidget = onFeatureEnabled('REPVISITS_GOOGLE_CALENDAR_SYNC')(GoogleCalendarSection);

class RepVisitsCalendarPage extends React.PureComponent {
  static propTypes = {
    router: PropTypes.object,
  };

  state = {
    currentDate: new Date(),
    currentView: 'month',
    importedEvents: [],
    visibleEventTypes: {
      fair: true,
      virtual: true,
      visit: true,
      pending: true,
    },
    currentEvent: null,
    notification: null,
    action: null,
    fromDateObj: getFromDate(new Date()),
    toDateObj: getToDate(new Date()),
  };

  onGoogleCalendarEvents = (googleEvents) => {
    const importedEvents = googleEvents.map((event) => ({
      type: 'google',
      allDay: !event.start.dateTime && !event.end.dateTime,
      start: new Date(Date.parse(event.start.dateTime || `${event.start.date}T00:00:00`)),
      startTimeZone: event.start.timeZone,
      end: new Date(Date.parse(event.end.dateTime || `${event.end.date}T00:00:00`)),
      endTimeZone: event.end.timeZone,
      title: event.summary || '',
      location: event.location || <FormattedMessage {...messages.detailsNotProvided} />,
      guests: event.attendees || <FormattedMessage {...messages.detailsNotProvided} />,
      description: event.description || <FormattedMessage {...messages.detailsNotProvided} />,
      seeMoreLink: event.htmlLink,
      backgroundColor: event.backgroundColor,
      borderColor: event.borderColor,
    }));
    this.setState({ importedEvents });
  };

  onReassignmentSuccess = (msg, kindOfToast) => {
    const toast = <Toast active title="" description={msg} onDeactivate={this.removeNotification} kind={kindOfToast} />;
    this.setState({ currentEvent: null, notification: toast });
  };

  setFromToRange = (firstDate, lastDay) => {
    this.setState({ fromDateObj: firstDate, toDateObj: lastDay });
  };

  toggleCloseButton = () => {
    this.setState((prevState) => ({ hideCloseButton: !prevState.hideCloseButton }));
  };

  handleView = (currentView) => {
    const { currentDate } = this.state;
    this.setState({ currentView, fromDateObj: getFromDate(currentDate), toDateObj: getToDate(currentDate) });
  };

  handleNavigate = (currentDate, currentView) => {
    this.setState({ currentDate, currentView, fromDateObj: getFromDate(currentDate), toDateObj: getToDate(currentDate) });
  };

  smallCalDayClick = (currentDate) => {
    this.setState({ currentDate });
  };

  handleEventFilter = (eventType) => {
    const { visibleEventTypes: oldTypes } = this.state;
    const toggledType = { [eventType]: !oldTypes[eventType] };
    const visibleEventTypes = { ...oldTypes, ...toggledType };
    this.setState({ visibleEventTypes });
  };

  hideAllEvents = () => {
    this.setState({
      visibleEventTypes: {
        fair: false,
        visit: false,
        virtual: false,
        pending: false,
      },
    });
  };

  closeSideBarWithNotification = (notif) => {
    this.setState({ currentEvent: null, notification: React.cloneElement(notif, { onDeactivate: this.removeNotification }) });
  };

  removeNotification = () => {
    this.setState({ notification: null });
  };

  closeSideBar = () => {
    if (this.state.currentEvent) {
      this.setState({ currentEvent: null });
    }
  };

  calEventClick = (event, synthE) => {
    synthE.stopPropagation();
    if (event.type === 'google') {
      this.setState({ currentEvent: event, action: actions.GOOGLE_CALENDAR_DETAILS });
    } else {
      this.setState({ currentEvent: event, notification: null, action: actions.CALENDAR_DRAWER });
    }
  };

  render() {
    const { router } = this.props;
    const components = {
      toolbar: Toolbar,
      event: Event,
      timeGutterHeader: timeGutterHeaderComponent,
      agenda: {
        event: EventAgenda,
        time: withEnabledFeatures(DateTimeAgenda),
        date: null,
      },
    };
    const { currentView, currentDate, currentEvent, visibleEventTypes, importedEvents, action, fromDateObj, toDateObj } = this.state;
    const fromDate = formatDate(fromDateObj);
    const toDate = formatDate(toDateObj);
    return (
      <SidebarWrapper>
        <PageTitle subTitle="Calendar" title="RepVisits" category="Intersect" />
        <SidebarWrapper.Content dimmed={!!currentEvent} onClick={this.closeSideBar}>
          <div styleName="calendar-container">
            <h1 id="pageTitle" styleName="view view-title">
              <FormattedMessage id="repvisits.menu.calendar" defaultMessage="Calendar" />
            </h1>
            <Grid centered styleName="view">
              <Grid.Row styleName="view-print">
                <Grid.Column width={4} styleName="left-panel-container">
                  <Segment data-cy="repvisits-calendar-left-panel" styleName="left-panel">
                    <LeftPanel
                      filterEventType={this.handleEventFilter}
                      visibleEventTypes={visibleEventTypes}
                      currentView={currentView}
                      currentDate={currentDate}
                      onDayClick={this.smallCalDayClick}
                      onNavigate={this.handleNavigate}
                      onHideAllEvents={this.hideAllEvents}
                      onReassignmentSuccess={this.onReassignmentSuccess}
                    >
                      <GoogleCalendarWidget
                        fromDate={fromDateObj.toISOString()}
                        toDate={toDateObj.toISOString()}
                        onEvents={this.onGoogleCalendarEvents}
                        router={router}
                      />
                    </LeftPanel>
                  </Segment>
                </Grid.Column>
                <Grid.Column width={12} styleName="big-cal-container" className="print-width-sixteen">
                  <Segment styleName="big-cal" className={`rbc-${currentView}`}>
                    <AvailabilityCalendar
                      visibleEventTypes={visibleEventTypes}
                      fromDate={fromDate}
                      toDate={toDate}
                      onView={this.handleView}
                      onNavigate={this.handleNavigate}
                      view={currentView}
                      date={currentDate}
                      components={components}
                      onSelectEvent={this.calEventClick}
                      importedEvents={importedEvents}
                      onDayClick={this.smallCalDayClick}
                      setFromToRange={this.setFromToRange}
                      fromDateObj={fromDateObj}
                      toDateObj={toDateObj}
                      length={calculateRangeLength(fromDateObj, toDateObj)}
                    />
                  </Segment>
                </Grid.Column>
              </Grid.Row>
            </Grid>
          </div>
          {this.state.notification}
        </SidebarWrapper.Content>
        <SidebarWrapper.Bar
          visible={!!currentEvent}
          onClose={this.closeSideBar}
        >
          {currentEvent && action === actions.CALENDAR_DRAWER && (
          <RepVisitsCalendarEventDetailsSideBar
            event={currentEvent}
            onSave={this.closeSideBarWithNotification}
            onReschedule={this.toggleCloseButton}
          />
          )}
          {(currentEvent && action === actions.GOOGLE_CALENDAR_DETAILS) && (
          <GoogleCalendarEventDetails
            event={currentEvent}
          />
          )}
        </SidebarWrapper.Bar>
      </SidebarWrapper>
    );
  }
}

export default RepVisitsCalendarPage;
